import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import {
  Mail,
  ChevronDown,
} from "lucide-react";
import { useLanguage } from "../providers/LanguageProvider";

const DesktopMenu = ({ menuItems, contactInfo, socialLinks }) => {
  const { toggleLanguage, translations, isRTL } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for menu background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      {/* make the menu take space and not fly above the content - <div className="h-16 hidden lg:block" /> */}
      {/* <header className="hidden lg:block fixed top-0 start-0 end-0 z-50 backdrop-blur-sm transition-colors duration-300 supports-[backdrop-blur]:bg-white/70 border-b border-neutral-200/50 bg-white/60"> */}
      <header 
        className={`hidden lg:block fixed top-0 start-0 end-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200/50 shadow-lg' 
            : 'bg-transparent'
        }`}
      >
        <div className="flex justify-between items-center h-16 px-6">
            <nav className={`flex items-center space-x-1 transition-all duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`}>
              {menuItems.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  className="relative group px-3 py-2 rounded-md text-neutral-600 hover:bg-neutral-100 transition-all duration-200 hover:shadow-md"
                >
                  <span className="font-medium text-sm">{item.title}</span>
                  <span className="absolute -bottom-1 inset-x-0 w-10/12 h-0.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </a>
              ))}

              <div className={`relative transition-all duration-300 ${isScrolled ? 'opacity-100' : 'opacity-0'}`}>
                <button className="relative peer group px-3 py-2 rounded-md text-neutral-600 hover:bg-neutral-100 transition-all duration-200 flex items-center space-s-2 hover:shadow-md">
                  <span className="transform group-hover:scale-110 transition-transform duration-200">
                    <Mail className="w-4 h-4" />
                  </span>
                  <span className="font-medium text-sm">{translations.menu.contact.button}</span>
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 peer-hover:rotate-180" />
                  <span className="absolute -bottom-1  inset-x-0 w-10/12 h-0.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </button>

                <div className="absolute top-full start-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-neutral-200 opacity-0 invisible peer-hover:opacity-90 peer-hover:visible hover:opacity-100 hover:visible transition-all duration-200 transform origin-top scale-95 peer-hover:scale-100 hover:scale-100">
                  <div className="p-4 space-y-4">
                    {contactInfo.map((item, index) => {
                      if (item?.link) {
                        return (
                          <a
                            key={index}
                            className="flex items-center text-neutral-600 hover:text-primary-600 transition-colors group"
                            href={item.link}
                          >
                            <span className="transform group-hover:scale-110 transition-transform duration-200">
                              {item.icon}
                            </span>
                            <span className="ms-3 font-medium">{item.text}</span>
                          </a>
                        )
                      } else {
                        return (
                          <div key={index} className="flex items-center text-neutral-600">
                            <span className="transform group-hover:scale-110 transition-transform duration-200">
                              {item.icon}
                            </span>
                            <span className="ms-3 font-medium">{item.text}</span>
                          </div>
                        )
                      }
                    })}
                  </div>
                </div>
              </div>
            </nav>

            <div className="flex items-center space-s-6">
              <div className="flex items-center space-s-4">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.link}
                    className={`text-neutral-600 hover:text-[${link.color}] transition-all duration-200 transform hover:scale-110 hover:-rotate-6`}
                    aria-label={link.label}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>

              <div className="h-6 w-px bg-gradient-to-b from-neutral-200/50 to-neutral-300/50 rounded-full" />

              <div className="flex items-center space-s-1">
                <button
                  className="relative group p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-all duration-200"
                  onClick={toggleLanguage}
                  aria-label="Toggle language"
                >
                  <div className="transform group-hover:scale-110 transition-transform duration-200">
                    <span className="w-5 h-5 flex items-center justify-center font-medium">
                      {isRTL ? "En" : "עב"}
                    </span>
                  </div>
                  <span className="absolute -bottom-1 ltr:start-1/2 rtl:end-1/2 -translate-x-1/2 w-10/12 h-0.5 bg-primary-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                </button>
              </div>
            </div>
          </div>
      </header>
    </>
  );
};

DesktopMenu.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired,
      icon: PropTypes.element,
    })
  ).isRequired,
  contactInfo: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element.isRequired,
      text: PropTypes.string.isRequired,
      link: PropTypes.string,
    })
  ).isRequired,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.element.isRequired,
      link: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

export default DesktopMenu;